// CPU pattern displacement for arbitrary param-graph meshes.
//
// The legacy vase shader path (vaseDeformShader.ts) GPU-displaces a unit
// cylinder via vertex shader uniforms — fast but ONLY works for the built-in
// vase primitive that owns its own mesh. Param-graph generated meshes
// (vase + lid + handles + custom CSG) live as plain triangles in
// app.triangles and have no shader hookup.
//
// This module bridges the gap: takes a TextureGrid (already rasterized from
// an SVG pattern by svgToTextureGrid) and pushes each vertex outward in
// cylindrical UV space — i.e. wraps the pattern around the mesh's vertical
// axis, treating (atan2(y,x), z) as (u, v).
//
// Why per-vertex independent and not welded? Param-graph triangles use
// independent Vec3 tuples per vertex. Two triangles sharing an edge happen
// to write the same (x,y,z) values in those tuples, but they are different
// JS arrays. Crucially, `sampleHeight(u,v)` is a deterministic function of
// position — two adjacent triangle vertices at the same world point sample
// identical heights and displace identically, so no cracks form. Skips the
// O(N log N) weld pass.

import type { Triangle } from './types';
import type { TextureGrid } from './vaseModel';

export interface PatternConfig {
  grid: TextureGrid;
  intensity: number;     // mm — how far the brightest pixel pushes a vertex
  repeatU: number;       // tile count around the cylinder (theta)
  repeatV: number;       // tile count vertically (z)
}

function sampleGridBilinear(grid: TextureGrid, u: number, v: number): number {
  // Wrap u/v into [0,1) via mod (handles negatives too via two-step normalize).
  let uu = u - Math.floor(u);
  let vv = v - Math.floor(v);
  if (uu < 0) uu += 1;
  if (vv < 0) vv += 1;
  const x = uu * grid.w;
  const y = vv * grid.h;
  const x0 = Math.floor(x) % grid.w;
  const y0 = Math.floor(y) % grid.h;
  const x1 = (x0 + 1) % grid.w;
  const y1 = (y0 + 1) % grid.h;
  const fx = x - Math.floor(x);
  const fy = y - Math.floor(y);
  const a = grid.data[y0 * grid.w + x0];
  const b = grid.data[y0 * grid.w + x1];
  const c = grid.data[y1 * grid.w + x0];
  const d = grid.data[y1 * grid.w + x1];
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

/** Push every triangle vertex radially outward (in the XY plane) by an
 *  amount sampled from the pattern at the vertex's (theta, z) → (u, v).
 *  Vertices on the Z axis (radius < 0.5mm) are not displaced — radial
 *  direction is undefined there. Returns a NEW triangle array with new
 *  recomputed normals; original input is untouched. */
export function applyPatternDisplacement(triangles: Triangle[], pattern: PatternConfig): Triangle[] {
  if (!pattern || !pattern.grid || triangles.length === 0) return triangles;
  // Mesh extents — z range used to normalize v coordinate to [0,1].
  let minZ = Infinity, maxZ = -Infinity;
  for (const t of triangles) {
    for (const v of [t.a, t.b, t.c]) {
      if (v[2] < minZ) minZ = v[2];
      if (v[2] > maxZ) maxZ = v[2];
    }
  }
  const zRange = Math.max(maxZ - minZ, 1);
  const intensity = pattern.intensity;
  const repU = Math.max(0.1, pattern.repeatU);
  const repV = Math.max(0.1, pattern.repeatV);
  const grid = pattern.grid;

  function displaceVertex(vx: number, vy: number, vz: number): [number, number, number] {
    const r = Math.hypot(vx, vy);
    if (r < 0.5) return [vx, vy, vz];   // on axis — no radial direction
    const theta = Math.atan2(vy, vx);
    const u = ((theta + Math.PI) / (2 * Math.PI)) * repU;
    const v = ((vz - minZ) / zRange) * repV;
    const h = sampleGridBilinear(grid, u, v);
    const push = h * intensity;
    const nr = r + push;
    return [(vx / r) * nr, (vy / r) * nr, vz];
  }

  const out: Triangle[] = new Array(triangles.length);
  for (let i = 0; i < triangles.length; i++) {
    const t = triangles[i];
    const a = displaceVertex(t.a[0], t.a[1], t.a[2]);
    const b = displaceVertex(t.b[0], t.b[1], t.b[2]);
    const c = displaceVertex(t.c[0], t.c[1], t.c[2]);
    // Recompute normal — displacement bends the surface so original normal is stale.
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx2 = c[0] - a[0], vy = c[1] - a[1], vz2 = c[2] - a[2];
    const nx = uy * vz2 - uz * vy;
    const ny = uz * vx2 - ux * vz2;
    const nz = ux * vy - uy * vx2;
    const nl = Math.hypot(nx, ny, nz) || 1;
    out[i] = { a, b, c, normal: [nx / nl, ny / nl, nz / nl] };
  }
  return out;
}
